import * as _ from 'lodash-es';
import {isSecurityKeysEqual} from '../../../models/security-key';
import {AspreaderSecurity, AspreaderSecurityConfig, AspreaderSecurityLeg} from './aspreader-security';

export const ASPREADER_SECURITY_NAME_SYMBOL_PARTS_SEPARATOR: string = '-';
export const ASPREADER_SECURITY_VER_PREFIX: string = '@';
export const ASPREADER_SECURITY_FIRST_VER = 1;

const ASPREADER_SECURITY_NAME_SYMBOL_PARTS_SEPARATOR_LEN = ASPREADER_SECURITY_NAME_SYMBOL_PARTS_SEPARATOR.length;
const ASPREADER_SECURITY_VER_PREFIX_LEN = ASPREADER_SECURITY_VER_PREFIX.length;

export interface AsecurityVersionInfo {
    rawName: string;
    name: string;
    versionNumber: number;
    userId: number;
}

const getLastPart = (str: string, pos?: number): string | undefined => {
    const separatorPos = str.lastIndexOf(ASPREADER_SECURITY_NAME_SYMBOL_PARTS_SEPARATOR, pos);

    return separatorPos >= 0
        ? str.substring(
            separatorPos + ASPREADER_SECURITY_NAME_SYMBOL_PARTS_SEPARATOR_LEN,
            (pos || pos === 0) ? pos + 1 : undefined
        )
        : undefined;
};

const getLastParts = (str: string, partCount: number): string[] => {
    const result: string[] = [];
    let lastPos = str.length - 1;

    if (str.length) {
        while (partCount > 0) {
            const lastPart = getLastPart(str, lastPos);

            if (lastPart !== undefined) {
                result.unshift(lastPart);
                lastPos = lastPos - lastPart.length - ASPREADER_SECURITY_NAME_SYMBOL_PARTS_SEPARATOR_LEN;
                partCount--;
            } else {
                break;
            }
        }
    }

    return result;
};

export const buildSymbolFromAsecurityVersionInfo = (
    params: Omit<AsecurityVersionInfo, 'name' | 'rawName' | 'versionNumber'> & Partial<AsecurityVersionInfo>
): string =>
    [
        params.rawName || appendAsecurityVersionToName(params.name, params.versionNumber),
        params.userId
    ]
        .filter(part => !!part)
        .join(ASPREADER_SECURITY_NAME_SYMBOL_PARTS_SEPARATOR);

export const parseAsecurityVersionFromSymbol = (symbol: string): AsecurityVersionInfo => {
    const result: AsecurityVersionInfo = {
        rawName:       undefined,
        name:          undefined,
        versionNumber: ASPREADER_SECURITY_FIRST_VER,
        userId:        undefined
    };

    const symbolLen = symbol.length;

    let rawNameEndPos = symbolLen;
    let nameEndPos = symbolLen;

    const intNumRegExp = /^\d+$/;
    const isIntNumberStr = (str: string): boolean => intNumRegExp.test(str);

    const parts = getLastParts(symbol, 2);
    const partsLen = parts.length;

    const possibleUserIdStr = parts[partsLen - 1];

    if (isIntNumberStr(possibleUserIdStr)) {
        result.userId = Number(possibleUserIdStr);
        rawNameEndPos = symbolLen
            - possibleUserIdStr.length
            - ASPREADER_SECURITY_NAME_SYMBOL_PARTS_SEPARATOR_LEN;
        nameEndPos = rawNameEndPos;

        const possibleVersionPart = parts[partsLen - 2];

        if (possibleVersionPart && _.startsWith(possibleVersionPart, ASPREADER_SECURITY_VER_PREFIX)) {
            const possibleVersionStr = possibleVersionPart.substring(ASPREADER_SECURITY_VER_PREFIX_LEN);

            if (isIntNumberStr(possibleVersionStr)) {
                result.versionNumber = Number(possibleVersionStr);
                nameEndPos = nameEndPos
                    - possibleVersionPart.length
                    - ASPREADER_SECURITY_NAME_SYMBOL_PARTS_SEPARATOR_LEN;
            }
        }
    }

    result.rawName = symbol.substring(0, rawNameEndPos);
    result.name = symbol.substring(0, nameEndPos);

    return result;
};

export interface LastAsecurityVersionInfo {
    lastVerNum: number | undefined;
    lastVerSecurity: AspreaderSecurity | undefined;
    lastVerInfo: AsecurityVersionInfo;
}

export const chooseLastAsecurityVersionByVerInfo = (
    allAsecurities: AspreaderSecurity[],
    verInfo: Pick<AsecurityVersionInfo, 'name' | 'userId'>
): LastAsecurityVersionInfo => {
    return allAsecurities.reduce<LastAsecurityVersionInfo>(
        (acc, asecurity) => {
            const itVerInfo = parseAsecurityVersionFromSymbol(asecurity.symbol);

            if (isAsecurityVersionInfoSameInSeries(itVerInfo, verInfo)) {
                if (!acc.lastVerSecurity || acc.lastVerNum < itVerInfo.versionNumber) {
                    acc.lastVerNum = itVerInfo.versionNumber;
                    acc.lastVerSecurity = asecurity;
                    acc.lastVerInfo = itVerInfo;
                }
            }

            return acc;
        },
        {
            lastVerNum:      undefined,
            lastVerSecurity: undefined,
            lastVerInfo:     undefined
        }
    );
};

export const chooseLastAsecurityVersionBySymbol = (
    allAsecurities: AspreaderSecurity[],
    symbol: string
): LastAsecurityVersionInfo =>
    chooseLastAsecurityVersionByVerInfo(allAsecurities, parseAsecurityVersionFromSymbol(symbol));

export const isAsecurityVersionInfoSameInSeries = (
    versionInfo1: Pick<AsecurityVersionInfo, 'name' | 'userId'>,
    versionInfo2: Pick<AsecurityVersionInfo, 'name' | 'userId'>
): boolean =>
    versionInfo1.userId === versionInfo2.userId
    && versionInfo1.name === versionInfo2.name;

export const appendAsecurityVersionToName = (name: string, versionNumber: number): string =>
    (versionNumber > ASPREADER_SECURITY_FIRST_VER)
        ? [
            name,
            [ASPREADER_SECURITY_VER_PREFIX, versionNumber].join('')
        ].join(ASPREADER_SECURITY_NAME_SYMBOL_PARTS_SEPARATOR)
        : name;

export const removeAsecurityVersionFromName = (rawName: string): string =>
    rawName
        ? parseAsecurityVersionFromSymbol(buildSymbolFromAsecurityVersionInfo({
            rawName,
            userId: 1
        })).name
        : '';

export const isAsecurityConfigEqual = (config1: AspreaderSecurityConfig, config2: AspreaderSecurityConfig): boolean =>
    config1.name === config2.name
    && config1.userId === config2.userId
    && isAsecurityConfigLegsEqual(config1.legs, config2.legs);

const isAsecurityConfigLegsEqual = (legs1: AspreaderSecurityLeg[], legs2: AspreaderSecurityLeg[]): boolean =>
    legs1.length === legs2.length
    && !legs1.some((leg1, iLeg1) => !isAsecurityConfigLegEqual(leg1, legs2[iLeg1]));

const isAsecurityConfigLegEqual = (leg1: AspreaderSecurityLeg, leg2: AspreaderSecurityLeg): boolean => {
    const convertNullToUndefined = (value: unknown | null): unknown | undefined =>
        (value === null) ? undefined : value;

    return convertNullToUndefined(leg1.side) === convertNullToUndefined(leg2.side)
        && convertNullToUndefined(leg1.qtyRatio) === convertNullToUndefined(leg2.qtyRatio)
        && convertNullToUndefined(leg1.priceRatio) === convertNullToUndefined(leg2.priceRatio)
        && convertNullToUndefined(leg1.activeQuoting) === convertNullToUndefined(leg2.activeQuoting)
        && convertNullToUndefined(leg1.payUp) === convertNullToUndefined(leg2.payUp)
        && convertNullToUndefined(leg1.stopTimeout) === convertNullToUndefined(leg2.stopTimeout)
        && isSecurityKeysEqual(leg1.securityKey, leg2.securityKey);
};
